import { useEffect } from 'react';
import { useAuth } from '@micronet/auth';
import { setTokenGetter } from '@micronet/clientreports';
import { configure } from './api/client';
import { configureStorageHub } from './api/storagehub';

type AccessTokenGetter = (
  forceRefresh?: boolean,
  options?: { silentOnly?: boolean }
) => Promise<string>;

// The error reporter needs a SYNCHRONOUS token reader: reportError runs on the
// unload path, where there is nothing left to await. So the most recent token is
// cached here and the reporter is handed a reader for the cache.
let cachedToken: string | null = null;
let refreshing = false;

// silentOnly, always: a background error report must NEVER open an auth popup
// (msal-provider.tsx:152-155) — the user took no action at all here. A silent
// acquisition that fails means the session is gone, so the cache is cleared rather
// than left holding a token that will 401.
function refreshCachedToken(getAccessToken: AccessTokenGetter): void {
  if (refreshing) return;
  refreshing = true;
  void getAccessToken(false, { silentOnly: true })
    .then((token) => {
      cachedToken = token || null;
    })
    .catch(() => {
      cachedToken = null;
    })
    .finally(() => {
      refreshing = false;
    });
}

// AuthBridge configures the API client with auth info from AuthContext.
export function AuthBridge() {
  const { getAccessToken, signOut } = useAuth();

  useEffect(() => {
    configure({ getAccessToken, onAuthFailure: (error) => void signOut(error) });
    // Also feed the token getter to the direct storage-hub metadata reader so
    // fetchFileMetadata attaches Authorization: Bearer (else it 401s).
    configureStorageHub({ getAccessToken });
    // And to the error reporter, which installReporter() could not be given: it
    // runs at module scope in main.tsx, before any auth provider exists. The getter
    // is read through a function on every send, never captured, because the real
    // one only exists after the first render. Reading also kicks off a refresh, so
    // a session that outlives one token keeps reporting.
    setTokenGetter(() => {
      refreshCachedToken(getAccessToken);
      return cachedToken;
    });
    refreshCachedToken(getAccessToken);
  }, [getAccessToken, signOut]);

  return null;
}
